import {inject, Service, signal} from '@angular/core'
import {DataService, LoanListItem} from './data.service'
import {BehaviorSubject} from 'rxjs'
import {LOCAL_STORAGE} from '../application/localstorage.provider'
import {Sort} from '@angular/material/sort'
import {DateTime} from 'luxon'
import {toObservable} from '@angular/core/rxjs-interop'
import {LetterSelector} from './letter-selector/letter-selector'

export const FILTER_VERSION = 2

export const BoundLoans = ['1 år', '2 år', '3 år', '4 år', '5 år', '7 år', '10 år']
export const UnBoundLoans = ['3 må', '2 må', '1 må']
export const BindSelectors = ['all', 'bound', 'unbound']

export const getEmptyFilter = (): FilterData => {
  return {
    version: FILTER_VERSION,
    changed: false,
    office: '',
    employee: '',
    productCode: 'all',
    bound: true,
    unbound: true,
    binding: ['all'],
    private: true,
    business: true,
    ownerType: ['all'],
    rebound: false,
    excluded: false,
    searchField: '',
    fromDate: null,
    toDate: null,
    sort: {active: '', direction: ''},
    ob: false,
    vb: false,
    discount: false,
    missingDiscount: false,
    haveDiscount: true
  }
}

export interface FilterDataBase {
  /**
   * Used to know if filter needs reset
   */
  version: number
  changed: boolean
  office: string
  employee: string
  productCode: string
  bound: boolean
  unbound: boolean
  binding: string[]
  business: boolean
  private: boolean
  rebound: boolean
  /**
   * If set it will show only loans that are excluded.
   */
  excluded: boolean
  ownerType: string[]
  searchField: string
  sort: Sort
  ob: boolean
  vb: boolean
  /**
   * Set this to true to filter for loans
   * that are due < 75 days.
   */
  discount: boolean
  /**
   * If true only loans that have no discount set is shown
   */
  missingDiscount: boolean
  /**
   * Have existing discount,
   */
  haveDiscount: boolean
}

export interface FilterData extends FilterDataBase {
  fromDate: DateTime | null
  toDate: DateTime | null
}

export interface StoredFilterData extends FilterDataBase {
  fromDate: string
  toDate: string
}

const FILTER_NAME = 'filter'

@Service()
/**
 * The point of this was to filter the list of loans
 */
export class FilterService {

  public filteredLoans$ = new BehaviorSubject<LoanListItem[]>([])

  public officeList = new BehaviorSubject<string[]>([])

  public employeeList = new BehaviorSubject<string[]>([])

  public productCodeList = new BehaviorSubject<string[]>([])

  public filter = signal(getEmptyFilter())

  public filter$ = toObservable(this.filter)
  /**
   * The size of filtered loans
   */
  public count = signal<number>(0)

  public totalCount = signal<number>(0)
  public digitalPercent = signal<string>('')

  private ownerTypes: string[] = ['B', 'P']

  private bindings: string[] = [...UnBoundLoans, ...BoundLoans]

  private loans: LoanListItem[] = []

  private pFilter: FilterData = getEmptyFilter()

  private readonly ils: Storage = inject(LOCAL_STORAGE)

  private readonly selector = inject(LetterSelector)

  private readonly dataService = inject(DataService)

  constructor() {
    this.dataService.loans$.subscribe((loans: LoanListItem[]) => {
      this.loans = loans
      this.officeList.next(this.getSet('office'))
      this.employeeList.next(this.getSet('responsible'))
      this.productCodeList.next(this.getSet('productCode'))
      // Load filter _after_ the loans so that it does not return empty
      this.totalCount.set(loans.length)
      this.loadFilter()
    })
  }

  public saveSort(sort: Sort): void {
    const existing: FilterData = JSON.parse(this.ils.getItem(FILTER_NAME)!)
    this.filter.update(f => {
      f.sort = sort
      return f
    })
    existing.sort = sort
    this.ils.setItem(FILTER_NAME, JSON.stringify(existing))
  }

  public setFilters(input: Partial<FilterData>): FilterData {
    this.pFilter = Object.assign(this.pFilter, input)
    this.ownerTypes = []
    this.bindings = []

    if (this.pFilter.binding.includes('all')) {
      this.bindings = [...UnBoundLoans, ...BoundLoans]
    } else {
      if (this.pFilter.binding.includes('bound')) {
        this.bindings = [...BoundLoans]
      }
      if (this.pFilter.binding.includes('unbound')) {
        this.bindings = [...this.bindings, ...UnBoundLoans]
      }
      this.bindings = [...new Set(input.binding?.filter(b => !BindSelectors.includes(b))),
        ...this.bindings]
    }

    if (this.pFilter.ownerType.includes('all')) {
      this.ownerTypes = ['P', 'B']
    }
    this.ownerTypes = [...new Set(
      [...this.ownerTypes,
        ...this.pFilter.ownerType.filter(o => o !== 'all')])]

    // Filter the loans and save the filter
    this.filteredLoans$.next(this.applyFilters(this.pFilter))
    this.count.set(this.filteredLoans$.value.length)
    this.ils.setItem(FILTER_NAME, JSON.stringify(this.pFilter))
    return this.pFilter
  }

  public resetFilter(): void {
    this.ils.removeItem(FILTER_NAME)
    const data = this.setFilters(getEmptyFilter())
    this.filteredLoans$.next(this.applyFilters(data))
    // When we reset, we also rest our signal
    this.filter.set(data)
    this.count.set(0)
  }

  public filterForProduct(loan: LoanListItem, filter: FilterData): boolean {
    if (filter.productCode === 'all') {
      return true
    }
    if (filter.productCode === 'own') {
      return loan.productCode !== 'Borgo'
    }
    return filter.productCode === loan.productCode
  }

  public filterForRebound(loan: LoanListItem, filter: FilterData): boolean {
    // If not rebound return the loan as is
    if (!filter.rebound) {
      return true
    }
    return this.selector.isReboundLoan(loan)
  }

  public getStoredFilter(): FilterData {
    let filter: FilterData = getEmptyFilter()
    try {
      const stored: StoredFilterData = JSON.parse(this.ils.getItem(FILTER_NAME) as string)
      filter = {...stored} as any

      if (stored.toDate) {
        filter.toDate = DateTime.fromISO(stored.toDate, {setZone: false})
      }
      if (filter.fromDate) {
        filter.fromDate = DateTime.fromISO(stored.fromDate)
      }
      // Reset filter if version has changed.
      if (filter.version !== FILTER_VERSION) {
        filter = getEmptyFilter()
      }
    } catch {
      this.ils.setItem(FILTER_NAME, JSON.stringify(filter))
    }
    return filter
  }


  private filterForExcluded(loan: LoanListItem, filter: FilterData): boolean {
    if (!filter.excluded) {
      return true
    }
    return filter.excluded && this.selector.loanIsExcluded(loan)
  }

  private filterForNoDiscount(loan: LoanListItem, filter: FilterData): boolean {
    if (!filter.missingDiscount) {
      return true
    }
    return this.selector.missingDiscount(loan)
  }

  private filterForHaveDiscount(loan: LoanListItem, filter: FilterData): boolean {
    return this.selector.haveDiscount(loan, filter.haveDiscount)
  }

  private loadFilter(): void {
    // Let all know we have a filter, it is either our empty filter or the one we find in local storage
    const filter = this.getStoredFilter()
    this.filter.set(filter)
    this.pFilter = filter
    this.setFilters(filter)
  }

  /**
   * Returns an array with unique members from the loan list items. E.g. for
   * offices ['Ystad', 'Lomma' ... ]
   *
   * @param property - The property of the loan list item.
   * @private
   */
  private getSet(property: string): string[] {

    return [...new Set(this.loans.map((loan: LoanListItem) => (loan as any)[property]))]
      .toSorted((a: string, b: string) => a.localeCompare(b, 'sv-SE'))
      .filter(i => i !== 'Saknas')
  }

  private applyFilters(filter: FilterData): LoanListItem[] {
    const from = (filter.fromDate as DateTime)?.toMillis()
    const to = (filter.toDate as DateTime)?.toMillis()
    const loans = this.loans
      .filter((loan: LoanListItem) => !from || from <= loan.renewalDate)
      .filter((loan: LoanListItem) => !to || to >= loan.renewalDate)
      .filter((loan: LoanListItem) => this.ownerTypes.length === 0 || this.ownerTypes.includes(loan.ownerType))
      .filter((loan: LoanListItem) => this.bindings.length === 0 || this.bindings.includes(loan.frequency))
      .filter(loan => filter.office === 'all' || filter.office === loan.office)
      .filter(loan => filter.employee === 'all' || filter.employee === loan.responsible)
      .filter(loan => this.filterForProduct(loan, filter))
      .filter(loan => this.filterForRebound(loan, filter))
      .filter(loan => this.filterForExcluded(loan, filter))
      .filter(loan => this.filterForNoDiscount(loan, filter))
      .filter(loan => this.filterForHaveDiscount(loan, filter))
      .filter((loan: LoanListItem) => {
        const fields = Object.values(loan) // Create an array of loan object properties as key-value pairs
          .filter(v => v)
          .map(val => (val + '').toLowerCase())
        return fields.join('').includes(filter.searchField.toLowerCase().trim()) // Join all values and see if search string is included
      })


    if (filter.ob) {
      return this.calcCarbon(this.selector.filterOB(loans))
    }

    if (filter.vb) {
      return this.calcCarbon(this.selector.filterVB(loans))
    }

    if (filter.discount) {
      return this.calcCarbon(this.selector.filterForDiscount(loans))
    }
    return this.calcCarbon(loans)
  }

  private calcCarbon(loans: LoanListItem[]) {
    const email = loans.filter(l => l.ib)
    const percent = email.length / loans.length * 100
    const carbon = ((loans.length * percent / 100) * 29 / 1000).toFixed(0)
    this.digitalPercent.set(`${(percent).toFixed(1)} % (${email.length} / ${loans.length}) kan skickas digitalt och spara ${carbon} kg CO₂`)
    return loans
  }
}
